import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Ensures the quick-add modal has a predictable summary/purchase stack above
 * long-form product content. Product-page HTML can be morphed after the modal
 * opens, so this helper is shared by the opener and the dialog observer.
 * @param {Element} root - The product grid or modal content element.
 * @param {{ productTitle?: string, productPageUrl?: string, priceSource?: Element | null }} [options]
 */
function prepareQuickAddModalSummary(root, options = {}) {
  const productDetails = root.querySelector('.product-details');
  if (!(productDetails instanceof HTMLElement)) return;

  const detailsContent = productDetails.querySelector(':scope > .group-block > .group-block-content');
  const contentContainer = detailsContent instanceof HTMLElement ? detailsContent : productDetails;

  const existingSummary = contentContainer.querySelector('.quick-add-modal__summary');
  const existingPurchase = contentContainer.querySelector('.quick-add-modal__purchase');

  const sourceHeader = Array.from(contentContainer.querySelectorAll(':scope > .group-block')).find((group) =>
    group.querySelector('product-price')
  );
  if (sourceHeader instanceof HTMLElement) sourceHeader.classList.add('quick-add-modal__source-header');

  const summary = existingSummary instanceof HTMLElement ? existingSummary : document.createElement('div');
  summary.className = 'quick-add-modal__summary';

  let title = summary.querySelector('.quick-add-modal__summary-title');
  if (!title) {
    title = document.createElement('div');
    title.className = 'quick-add-modal__summary-title';
    summary.appendChild(title);
  }

  const titleText =
    options.productTitle ||
    root.querySelector('.view-product-title a')?.textContent?.trim() ||
    root.querySelector('.quick-add-modal__source-header .text-block h1')?.textContent?.trim() ||
    root.querySelector('.text-block h1')?.textContent?.trim() ||
    '';

  if (titleText) {
    let titleLink = title.querySelector('a');
    if (!titleLink) {
      titleLink = document.createElement('a');
      title.appendChild(titleLink);
    }
    titleLink.href = options.productPageUrl || root.querySelector('.view-product-title a')?.getAttribute('href') || '#';
    titleLink.textContent = titleText;
  }

  const summaryPrice = summary.querySelector('product-price');
  const priceSource =
    options.priceSource ||
    sourceHeader?.querySelector('product-price') ||
    root.querySelector('.product-details product-price');

  if (!summaryPrice && priceSource) {
    const price = priceSource.cloneNode(true);
    if (price instanceof HTMLElement) {
      // Anchors the modal's price sizing overrides (see quick-add-modal-styles).
      price.classList.add('product-price');
      price.classList.add('quick-add-modal__summary-price');
      summary.appendChild(price);
    }
  }

  const purchase = existingPurchase instanceof HTMLElement ? existingPurchase : document.createElement('div');
  purchase.className = 'quick-add-modal__purchase';

  const variantPicker = Array.from(productDetails.querySelectorAll('variant-picker')).find(
    (element) => !element.closest('.quick-add-modal__purchase')
  );
  const buyButtons = Array.from(productDetails.querySelectorAll('.buy-buttons-block')).find(
    (element) => !element.closest('.quick-add-modal__purchase')
  );

  if (variantPicker) purchase.appendChild(variantPicker);
  if (buyButtons) purchase.appendChild(buyControlsRoot(buyButtons));

  if (!existingPurchase && purchase.childElementCount > 0) contentContainer.prepend(purchase);
  if (!existingSummary) contentContainer.prepend(summary);
}

/**
 * Returns the node to relocate when moving buy controls: buy buttons can be
 * wrapped in an early-access gate on the product page, and hoisting that whole
 * gate keeps its lock state and access-code panel working in whatever surface
 * the controls land in — e.g. inside the quick-add modal.
 * @param {Element} element - The buy control that was found.
 * @returns {Element} The enclosing early-access-gate if present, else the element itself.
 */
function buyControlsRoot(element) {
  return /** @type {Element} */ (element.closest('early-access-gate') ?? element);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleQuickAddButtonClick);
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleQuickAddButtonClick);
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Native fallback for browsers that miss the delegated `on:click` handler.
   * @param {Event} event
   */
  #handleQuickAddButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.quick-add__button--choose')) return;
    if (event.defaultPrevented) return;

    this.handleClick(event);
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#playPressAnimation();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);
    const shouldOpenBeforeFetch = isMobileBreakpoint() && !productGrid;

    if (shouldOpenBeforeFetch) {
      this.#renderLoadingModalContent(currentUrl);
      this.#openQuickAddModal();
    }

    if (!productGrid) {
      // Fetch and cache the content
      this.toggleAttribute('loading', true);

      try {
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      } catch (error) {
        this.#renderQuickAddError(currentUrl);
        console.warn('[quick-add] Unable to load quick-add product content:', error);
        return;
      } finally {
        this.toggleAttribute('loading', false);
      }
    }

    if (!productGrid) {
      this.#renderQuickAddError(currentUrl);
      return;
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    if (!this.#isQuickAddModalOpen()) this.#openQuickAddModal();
  };

  /**
   * Plays a short press animation on the quick-add button so the click is
   * acknowledged immediately, before the modal has anything to show.
   */
  #playPressAnimation() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const button = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add__button--choose'));
    if (!button) return;

    button.removeAttribute('data-pressed');
    // Force a reflow so the animation restarts when the button is clicked again mid-animation.
    void button.offsetWidth;
    button.setAttribute('data-pressed', '');

    button.addEventListener('animationend', () => button.removeAttribute('data-pressed'), { once: true });
  }

  /**
   * Builds the modal's media element from the image already rendered on the card.
   * @returns {HTMLElement | null} The media element, or null when the product has no image.
   */
  #buildModalMedia() {
    const template = this.querySelector('template[data-quick-add-media]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    const media = document.createElement('div');
    // Keeps `product-information__media` so the modal's existing grid placement applies.
    media.className = 'product-information__media quick-add-modal__media';
    media.appendChild(template.content.cloneNode(true));

    return media;
  }

  /**
   * Opens mobile Safari immediately with visible feedback while the product
   * page response is still in flight.
   * @param {string} productPageUrl
   */
  #renderLoadingModalContent(productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const fragment = document.createDocumentFragment();
    const cardMedia = this.#buildModalMedia();
    if (cardMedia) fragment.appendChild(cardMedia);

    fragment.appendChild(this.#buildFallbackHeader(productPageUrl));

    const status = document.createElement('div');
    status.className = 'quick-add-modal__loading';
    status.setAttribute('role', 'status');
    status.textContent = 'Loading options...';
    fragment.appendChild(status);

    modalContent.setAttribute('aria-busy', 'true');
    modalContent.replaceChildren(fragment);
  }

  /**
   * @param {string} productPageUrl
   */
  #renderQuickAddError(productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (!this.#isQuickAddModalOpen()) this.#openQuickAddModal();

    const error = document.createElement('div');
    error.className = 'quick-add-modal__loading';
    error.setAttribute('role', 'status');
    error.textContent = 'Unable to load options. View full details to continue.';

    const link = document.createElement('a');
    link.className = 'view-more-details';
    link.href = productPageUrl;
    link.textContent = 'View full details';

    modalContent.setAttribute('aria-busy', 'false');
    modalContent.querySelector('.quick-add-modal__loading')?.remove();
    modalContent.append(error, link);
  }

  /**
   * @param {string} productPageUrl
   * @returns {HTMLElement}
   */
  #buildFallbackHeader(productPageUrl) {
    const productHeader = document.createElement('div');
    productHeader.className = 'product-header';

    const titleLink = document.createElement('a');
    titleLink.textContent = this.dataset.productTitle || '';
    titleLink.href = productPageUrl;

    const titleWrap = document.createElement('div');
    titleWrap.className = 'view-product-title';
    titleWrap.appendChild(titleLink);
    productHeader.appendChild(titleWrap);

    const productPrice = this.closest('product-card')?.querySelector('product-price');
    if (productPrice) productHeader.appendChild(productPrice.cloneNode(true));

    return productHeader;
  }

  #isQuickAddModalOpen() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    return dialogComponent instanceof QuickAddDialog && Boolean(dialogComponent.refs?.dialog?.open);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const scrollContainers = dialogComponent.querySelectorAll(
      '.quick-add-modal, .quick-add-modal__content, .product-details, .product-information__media'
    );

    scrollContainers.forEach((container) => {
      if (container instanceof HTMLElement) container.scrollTop = 0;
    });
  }

  #scheduleScrollReset() {
    this.#resetScroll();
    requestAnimationFrame(() => this.#resetScroll());
    requestAnimationFrame(() => requestAnimationFrame(() => this.#resetScroll()));
    setTimeout(() => this.#resetScroll(), 120);
    setTimeout(() => this.#resetScroll(), 320);
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    if (!dialogComponent.showDialog()) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    const remeasure = () => {
      const modalContent = document.getElementById('quick-add-modal-content');
      const pdc = modalContent?.querySelector('product-description-toggle');
      if (pdc && typeof pdc.measure === 'function') {
        pdc.measure();
        requestAnimationFrame(() => pdc.measure());
        setTimeout(() => pdc.measure(), 180);
      }
    };
    this.#scheduleScrollReset();
    dialog.addEventListener('animationstart', () => { this.#scheduleScrollReset(); remeasure(); }, { once: true });
    setTimeout(() => { this.#scheduleScrollReset(); remeasure(); }, 220);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  #enrichModalContent(productGrid) {
    const viewMore = productGrid.querySelector('.view-more-details__wrapper a, .view-product-title a');
    if (viewMore) {
      const wrapper = productGrid.querySelector('.view-more-details__wrapper');
      if (wrapper && !wrapper.querySelector('.view-more-details')) {
        const link = document.createElement('a');
        link.className = 'view-more-details';
        link.href = this.productPageUrl;
        link.textContent = 'View full details';
        wrapper.innerHTML = '';
        wrapper.appendChild(link);
      }
    }
  }

  /**
   * Builds the summary/purchase area the quick-add modal needs, independent of
   * the full product page's visual ordering.
   * @param {Element} productGrid - The product grid element
   */
  #prepareDesktopModalContent(productGrid) {
    prepareQuickAddModalSummary(productGrid, {
      productTitle: this.dataset.productTitle,
      productPageUrl: this.productPageUrl,
      priceSource: this.closest('product-card')?.querySelector('product-price'),
    });
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    this.#enrichModalContent(productGrid);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      const titleLink = document.createElement('a');
      titleLink.textContent = this.dataset.productTitle || '';
      titleLink.href = this.productPageUrl;
      const titleWrap = document.createElement('div');
      titleWrap.className = 'view-product-title';
      titleWrap.appendChild(titleLink);
      productHeader.appendChild(titleWrap);
      const productPrice = productGrid.querySelector('product-price');
      if (productPrice) {
        const headerPrice = /** @type {HTMLElement} */ (productPrice.cloneNode(true));
        // Anchors the modal's price sizing overrides (see quick-add-modal-styles).
        headerPrice.classList.add('product-price');
        productHeader.appendChild(headerPrice);
      }
      productGrid.appendChild(productHeader);

      const blocksToMove = productGrid.querySelectorAll('variant-picker, product-description-toggle, product-specifications, product-inventory, .quick-add-modal__trust, product-form-component, .view-more-details__wrapper');
      const movedNodes = new Set();
      blocksToMove.forEach((el) => {
        const node = buyControlsRoot(el);
        if (movedNodes.has(node)) return;
        movedNodes.add(node);
        productGrid.appendChild(node);
      });
      productDetails?.remove();
    } else {
      this.#prepareDesktopModalContent(productGrid);
    }

    // Swap the fetched gallery for the card's own image. The product page renders a
    // custom stacked gallery of every media item, which does not fit the modal's
    // single-image slot.
    const cardMedia = this.#buildModalMedia();
    if (cardMedia) {
      const fetchedMedia = productGrid.querySelector('.product-information__media');
      if (fetchedMedia) {
        fetchedMedia.replaceWith(cardMedia);
      } else {
        productGrid.prepend(cardMedia);
      }
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);
    modalContent.setAttribute('aria-busy', 'false');

    this.#syncVariantSelection(modalContent);
    this.#scheduleScrollReset();
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {MutationObserver | null} */
  #modalContentObserver = null;
  /** @type {number | null} */
  #summaryFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    const modalContent = this.querySelector('#quick-add-modal-content');
    if (modalContent) {
      this.#modalContentObserver = new MutationObserver(this.#scheduleModalSummary);
      this.#modalContentObserver.observe(modalContent, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#modalContentObserver?.disconnect();
    if (this.#summaryFrame) cancelAnimationFrame(this.#summaryFrame);
  }

  #scheduleModalSummary = () => {
    if (this.#summaryFrame) return;

    this.#summaryFrame = requestAnimationFrame(() => {
      this.#summaryFrame = null;
      this.#ensureModalSummary();
    });
  };

  #ensureModalSummary(options = {}) {
    const modalContent = this.querySelector('#quick-add-modal-content');
    if (!modalContent) return;

    prepareQuickAddModalSummary(modalContent, options);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        this.#ensureModalSummary({
          productTitle: event.product?.title,
          productPageUrl: this.querySelector('.view-product-title a')?.getAttribute('href') || undefined,
        });

        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
